// @ts-nocheck
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    ActivityIndicator,
    FlatList,
    Text,
    TouchableOpacity,
    View,
    RefreshControl,
} from 'react-native';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import Header from '../../components/Header/Header';
import { styles } from './styles';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import TemporaryPasswordBanner from '../../components/TemporaryPasswordBanner/TemporaryPasswordBanner';
import { useUserStore } from '../../store/UserStore';
import { AppRoutes } from '../../navigation/Routes.types';
import ContainersService from '../../services/ContainersService/ContainersService';
import { Colors } from '../../constants/Colors';
import { useAuthStore } from '../../store/AuthStore';
import { getAnalytics, logEvent } from '@react-native-firebase/analytics';
import { checkDigitalGate } from '../../services/DigitalGateService/DigitalGateService';
import { useLimitStore } from '../../store/LimitStore';

const Containers = () => {
    const navigation = useNavigation<any>();
    const { goBack, navigate } = navigation;
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [data, setData] = useState<any[]>([]);
    const [error, setError] = useState('');
    const [statuses, setStatuses] = useState([]);
    const [loadingItem, setLoadingItem] = useState<number | null>(null);
    const route = useRoute<any>();
    const analyticsInstance = getAnalytics();
    const limitDigital = useLimitStore(state => state.digital);
    const temporaryPasswordLogin = useUserStore(state => state.temporaryPasswordLogin);
    const [digitalEnabled, setDigitalEnabled] = useState<boolean | null>(limitDigital ?? null);
    const [digitalGateLoading, setDigitalGateLoading] = useState<boolean>(false);
    const [digitalGateError, setDigitalGateError] = useState<string | null>(null);
    const digitalStatusRef = useRef<boolean | null>(limitDigital ?? null);
    const isDigitalClient = digitalEnabled === true;
    const isLoading = loading || digitalGateLoading;

    useEffect(() => {
        const nextValue = limitDigital ?? null;
        digitalStatusRef.current = nextValue;
        setDigitalEnabled(nextValue);
    }, [limitDigital]);

    const refreshDigitalGate = useCallback(async () => {
        setDigitalGateLoading(true);
        setDigitalGateError(null);
        try {
            const result = await checkDigitalGate();
            const value = result.value ?? null;
            digitalStatusRef.current = value;
            setDigitalEnabled(value);
            setDigitalGateError(result.error ?? null);
            return value;
        } finally {
            setDigitalGateLoading(false);
        }
    }, []);
    const handleNavigation = async () => {
        await logEvent(analyticsInstance, 'navigate_to_containers_request', {
            screen: 'Containers',
            purpose: 'User wants to request containers',
        });
        navigate(AppRoutes.CONTAINERS_REQUEST);
    }
    const clientCode = useAuthStore()?.user?.current_client?.clientCode || useAuthStore()?.user?.clientCode;
    const fetchData = useCallback(async (status?: boolean | null) => {
        const allowFetch = (status ?? digitalStatusRef.current) === true;

        if (!allowFetch) {
            setLoading(false);
            setRefreshing(false);
            setData([]);
            setError('');
            return;
        }

        setLoading(true);
        setRefreshing(true);
        try {
            const statuses = await ContainersService.ContainerRequestStatusesGet(clientCode);
            setStatuses(statuses);
            const response = await ContainersService.ContainerGet(clientCode);
            setData(response);
            if (response.length === 0) {
                setError('Nenhuma solicitação encontrada.');
            } else {
                setError('');
            }
        } catch (error) {
            setError('Erro ao carregar as solicitações.');
            console.error(error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [clientCode]);

    const updateRequest = async (id: number, status: string) => {
        setLoadingItem(id);
        try {
            await ContainersService.updateContainerStatus(String(id), status);
            fetchData();
        } catch (error) {
            console.error('Erro ao atualizar status do pedido:', error);
        } finally {
            setLoadingItem(null);
        }
    }

    useFocusEffect(
        useCallback(() => {
            let isActive = true;

            const load = async () => {
                const status = await refreshDigitalGate();
                if (!isActive) {
                    return;
                }
                await fetchData(status);
            };

            load();

            return () => {
                isActive = false;
            };
        }, [refreshDigitalGate, fetchData]),
    );

    const onRefresh = useCallback(() => {
        setRefreshing(true);
        (async () => {
            const status = await refreshDigitalGate();
            await fetchData(status);
        })();
    }, [refreshDigitalGate, fetchData]);

    const renderItem = ({ item }: { item: any }) => (
        <View style={styles.itemContainer}>
            <Text style={styles.itemText}>Pedido: {item.id}</Text>
            <Text style={styles.itemText}>Status: {item.status.status_name.charAt(0).toUpperCase() + item.status.status_name.slice(1)}</Text>
            <Text style={styles.itemText}>Criado em: {new Date(item.created_at).toLocaleDateString()}</Text>
            <Text style={styles.itemText}>Vasilhames:</Text>
            {item.items.map((subItem: any) => (
                <View key={subItem.id} style={styles.subItemContainer}>
                    <Text style={styles.subItemText}>Vasilhame: {subItem.product_name}</Text>
                    <Text style={styles.subItemText}>Unidades: {subItem.quantity}</Text>
                </View>
            ))}
            {item.status.id === 5 && (<Text style={styles.filterText}>Vasilhames com indisponibilidade no momento, solicite novamente no futuro!</Text>)}
            {item.status.id == 1 && <ButtonComponent
                outlined
                title='Cancelar Solicitação'
                disabled={temporaryPasswordLogin}
                onPress={() => updateRequest(item.id, 'cancelado')}
                isLoading={loadingItem === item.id}
            />}
        </View>
    );

    return (
        <View style={styles.container}>
            <View style={styles.containerHeader}>
                <SearchHeader onPress={goBack} title="Vasilhames" />
                <Text style={styles.description}>
                    Aqui você pode solicitar e acompanhar seus pedidos de vasilhames de forma fácil e rápida!
                </Text>
            </View>
            <TemporaryPasswordBanner />

            {isLoading && <ActivityIndicator size="large" color={Colors.RedPallete.red} />}

            {!isLoading && digitalEnabled === false && (
                <View style={styles.centerMessage}>
                    <View style={styles.containerMessage}>
                        <Text style={styles.messageText}>
                            Para solicitar vasilhames, entre em contato com seu vendedor.
                        </Text>
                    </View>
                </View>
            )}

            {!isLoading && digitalGateError && digitalEnabled !== true && (
                <Text style={styles.errorText}>{digitalGateError}</Text>
            )}

            {!isLoading && !digitalGateError && error && isDigitalClient && (
                <Text style={styles.errorText}>{error}</Text>
            )}

            {!isLoading && !error && !digitalGateError && isDigitalClient && (
                <FlatList
                    data={data}
                    renderItem={renderItem}
                    keyExtractor={(item) => item.id.toString()}
                    refreshControl={
                        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
                    }
                />
            )}

            {isDigitalClient && (
                <View style={styles.containerFooter}>
                    <ButtonComponent
                        title='Solicitar vasilhames'
                        disabled={temporaryPasswordLogin}
                        onPress={handleNavigation}
                    />
                </View>
            )}
        </View>
    );
};

export default Containers;
